import React, { useEffect, useState } from "react";
import { AppBar, Toolbar, IconButton, Typography, Box, ButtonBase, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import HomeIcon from "@mui/icons-material/Home";
import NotificationIcon, { subscribeToNotifications } from "./NotificationIcon";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import { Link, useLocation } from "react-router-dom";
import { SIDEBAR_WIDTH } from "../theme";
import logo from "../imgs/wandr-logo.svg";

const BottomToolbar = () => {
  const [hasNewNotifications, setHasNewNotifications] = useState(false);
  const location = useLocation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));


  useEffect(() => {
    const unsubscribeFunc = subscribeToNotifications(setHasNewNotifications);

    return () => {
      if (typeof unsubscribeFunc === "function") {
        unsubscribeFunc();
      }
    };
  }, []);

  // Lets index.css push the page and its fixed app bars clear of the sidebar.
  useEffect(() => {
    if (!isDesktop) return undefined;
    document.body.classList.add("has-sidebar");
    return () => document.body.classList.remove("has-sidebar");
  }, [isDesktop]);


  const classes = {
    appBar: {
      top: "auto",
      bottom: 0,
      borderTop: "1px solid rgba(130, 245, 184, 0.25)",
      boxShadow: "0 -8px 24px rgba(0, 0, 0, 0.4)",
      // On the AppBar (not the Toolbar) so the bar stays full width while its icons are capped.
      background: 'linear-gradient(125deg, #0f1f19, #0a1712)',
    },
    toolbar: {
      justifyContent: 'space-around',
    },
    iconButton: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      color: "#d7ffeb",
      px: 1.2,
      py: 0.8,
      borderRadius: 2,
      position: "relative",
      transition: "transform 0.2s ease, color 0.2s ease, background-color 0.2s ease",
      "&:hover": {
        transform: "translateY(-2px)",
        color: "#7cf4b5",
      },
    },
    activeButton: {
      color: "#7cf4b5",
      backgroundColor: "rgba(115, 240, 174, 0.14)",
      "&::after": {
        content: '""',
        position: "absolute",
        bottom: -6,
        width: 26,
        height: 3,
        borderRadius: 99,
        background: "linear-gradient(90deg, #22c55e, #6ee7b7)",
        boxShadow: "0 0 12px rgba(34, 197, 94, 0.6)",
      },
    },
    icon: {
      color: "inherit",
    },
    text: {
      color: "inherit",
      fontWeight: 600,
      // Icons only on phones; labels from tablet width up.
      display: { xs: "none", sm: "block" },
    },
    sidebar: {
      position: "fixed",
      top: 0,
      left: 0,
      bottom: 0,
      width: SIDEBAR_WIDTH,
      // Above the page's fixed app bars (MUI AppBar zIndex is 1100).
      zIndex: 1200,
      display: "flex",
      flexDirection: "column",
      gap: 0.5,
      px: 2,
      py: 3,
      background: "linear-gradient(180deg, #0f1f19, #0a1712)",
      borderRight: "1px solid rgba(130, 245, 184, 0.25)",
      boxShadow: "8px 0 24px rgba(0, 0, 0, 0.4)",
      overflowY: "auto",
    },
    sidebarLogo: {
      display: "block",
      width: 120,
      height: 120,
      mx: "auto",
      mb: 3,
    },
    sidebarItem: {
      justifyContent: "flex-start",
      gap: 1.5,
      width: "100%",
      px: 1.5,
      py: 1.2,
      borderRadius: 2,
      color: "#d7ffeb",
      position: "relative",
      transition: "color 0.2s ease, background-color 0.2s ease",
      "&:hover": {
        color: "#7cf4b5",
        backgroundColor: "rgba(115, 240, 174, 0.08)",
      },
    },
    sidebarActive: {
      color: "#7cf4b5",
      backgroundColor: "rgba(115, 240, 174, 0.14)",
      "&::before": {
        content: '""',
        position: "absolute",
        left: -16,
        top: 8,
        bottom: 8,
        width: 3,
        borderRadius: 99,
        background: "linear-gradient(180deg, #22c55e, #6ee7b7)",
        boxShadow: "0 0 12px rgba(34, 197, 94, 0.6)",
      },
    },
    sidebarText: {
      color: "inherit",
      fontWeight: 600,
      fontSize: 15,
    },
  };

  const navItems = [
    { label: "Inicio", to: "/home", icon: <HomeIcon sx={classes.icon} /> },
    { label: "Viajes", to: "/trips", icon: <FormatListBulletedIcon sx={classes.icon} /> },
    { label: "Chats", to: "/chats", icon: <ChatBubbleOutlineIcon sx={classes.icon} /> },
    { label: "Publicar", to: "/publish", icon: <AddCircleOutlineIcon sx={classes.icon} /> },
    {
      label: "Notificaciones",
      to: "/notifications",
      icon: <NotificationIcon hasNewNotifications={hasNewNotifications} sx={classes.icon} />,
    },
    { label: "Perfil", to: "/profile", icon: <AccountCircleIcon sx={classes.icon} /> },
  ];

  // Sidebar only, pinned to its bottom; on phones these live in Perfil.
  const secondaryItems = [
    { label: "Soporte", to: "/support", icon: <SupportAgentIcon sx={classes.icon} /> },
  ];

  const isActiveRoute = (to) => location.pathname === to || location.pathname.startsWith(`${to}/`);


  if (isDesktop) {
    const renderSidebarItem = (item) => {
      const isActive = isActiveRoute(item.to);

      return (
        <ButtonBase
          key={item.to}
          component={Link}
          to={item.to}
          aria-current={isActive ? "page" : undefined}
          sx={{
            ...classes.sidebarItem,
            ...(isActive ? classes.sidebarActive : {}),
          }}
        >
          {item.icon}
          <Typography sx={classes.sidebarText}>{item.label}</Typography>
        </ButtonBase>
      );
    };

    return (
      <Box component="nav" aria-label="Navegación principal" sx={classes.sidebar}>
        <Link to="/home" aria-label="Inicio">
          <Box component="img" src={logo} alt="Wandr" sx={classes.sidebarLogo} />
        </Link>
        {navItems.map(renderSidebarItem)}
        <Box sx={{ mt: "auto", pt: 2 }}>{secondaryItems.map(renderSidebarItem)}</Box>
      </Box>
    );
  }

  return (
    <AppBar position="fixed" sx={classes.appBar}>
      <Toolbar sx={classes.toolbar}>
        {navItems.map((item) => {
          const isActive = isActiveRoute(item.to);

          return (
            <IconButton
              key={item.to}
              component={Link}
              to={item.to}
              aria-label={item.label}
              sx={{
                ...classes.iconButton,
                ...(isActive ? classes.activeButton : {}),
              }}
            >
              {item.icon}
              <Typography variant="caption" fontSize={9} sx={classes.text}>
                {item.label}
              </Typography>
            </IconButton>
          );
        })}
      </Toolbar>
    </AppBar>
  );
};

export default BottomToolbar;
